import { Pencil, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { type ReactNode, useCallback, useEffect, useState } from 'react'

import { stringifyGraph } from '@/clients/http'
import { useAuth } from '@/contexts/AuthContext'
import { apiErrorMessage } from '@/lib/apiError'
import type { ResourceApi } from '@/http/models/Resource'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { NoData } from '@/components/ui/data-table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Pagination } from '@/components/ui/pagination-controls'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'

import { ActionDropdown, type ActionItem, ConfirmDialog, PageHeader } from '@/components/atoms'

export interface ResourceField {
  name: string
  label: string
  type: 'text' | 'email' | 'textarea' | 'number' | 'select' | 'switch' | 'status'
  required?: boolean
  placeholder?: string
  help?: string
  options?: { label: string; value: string }[]
  min?: number
  max?: number
}

export interface ResourceColumn<T> {
  label: string
  render: (row: T) => ReactNode
  className?: string
}

interface Props<T extends { _id: string }> {
  title: string
  description: string
  /** Lower-case noun for buttons and messages, e.g. "supplier". */
  noun: string
  api: ResourceApi<T>
  permissions: { create: string; update: string; delete: string }
  columns: ResourceColumn<T>[]
  fields: ResourceField[]
  /** Values for the form: the row being edited, or defaults when `row` is undefined. */
  toForm: (row?: T) => Record<string, unknown>
  graph?: Record<string, unknown>
  searchPlaceholder?: string
  /** More row actions (e.g. branch staff). */
  rowActions?: (row: T, refresh: () => void) => ActionItem[]
  /** Rendered beside the search box, e.g. a filter. */
  children?: ReactNode
  /** Shown in the delete confirmation; defaults to the soft-delete wording. */
  deleteDescription?: string
}

const PAGE_SIZE = 20

/** Empty optional strings are left out: the server's zod rules reject an empty email. */
const toBody = (fields: ResourceField[], form: Record<string, unknown>) => {
  const body: Record<string, unknown> = {}
  for (const field of fields) {
    const value = form[field.name]
    if (field.type === 'number') {
      if (value !== '' && value !== undefined && value !== null) body[field.name] = Number(value)
    } else if (field.type === 'switch') {
      body[field.name] = !!value
    } else if (field.type === 'status') {
      body[field.name] = value ? 'active' : 'inactive'
    } else if (typeof value === 'string') {
      const trimmed = value.trim()
      if (trimmed || (field.type !== 'email' && field.type !== 'select')) body[field.name] = trimmed
    }
  }
  return body
}

/**
 * List / create / edit / delete page for a plain backoffice resource (suppliers, units, price books, membership
 * tiers, branches, terminals). Searches with the server's `search`, pages with `page` / `length`.
 */
export function ResourcePage<T extends { _id: string }>({
  title,
  description,
  noun,
  api,
  permissions,
  columns,
  fields,
  toForm,
  graph,
  searchPlaceholder,
  rowActions,
  children,
  deleteDescription,
}: Props<T>) {
  const { can } = useAuth()
  const [rows, setRows] = useState<T[]>([])
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const [editing, setEditing] = useState<T | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState<Record<string, unknown>>({})
  const [saving, setSaving] = useState(false)
  const [toDelete, setToDelete] = useState<T | null>(null)
  const [deleting, setDeleting] = useState(false)

  const graphEnc = graph ? stringifyGraph(graph) : undefined

  const load = useCallback(async () => {
    setLoading(true)
    const res = await api.getList({ page, length: PAGE_SIZE, search: debouncedSearch || undefined, graph_enc: graphEnc })
    if (res.success && res.data) {
      setRows(res.data.result ?? [])
      setCount(res.data.count ?? 0)
    } else {
      toast.error(apiErrorMessage(res, `Could not load ${noun}s`))
    }
    setLoading(false)
  }, [api, page, debouncedSearch, graphEnc, noun])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 400)
    return () => clearTimeout(timer)
  }, [search])

  const openCreate = () => {
    setEditing(null)
    setForm(toForm())
    setDialogOpen(true)
  }

  const openEdit = (row: T) => {
    setEditing(row)
    setForm(toForm(row))
    setDialogOpen(true)
  }

  const save = async () => {
    const missing = fields.find((f) => f.required && (form[f.name] === '' || form[f.name] === undefined || form[f.name] === null))
    if (missing) {
      toast.error(`${missing.label} is required`)
      return
    }
    setSaving(true)
    const body = toBody(fields, form)
    const res = editing ? await api.update(editing._id, body) : await api.create(body)
    setSaving(false)
    if (!res.success) {
      toast.error(apiErrorMessage(res, `Could not save the ${noun}`))
      return
    }
    toast.success(editing ? `${noun[0].toUpperCase()}${noun.slice(1)} updated` : `${noun[0].toUpperCase()}${noun.slice(1)} created`)
    setDialogOpen(false)
    load()
  }

  const confirmDelete = async () => {
    if (!toDelete) return
    setDeleting(true)
    const res = await api.remove(toDelete._id)
    setDeleting(false)
    if (!res.success) {
      toast.error(apiErrorMessage(res, `Could not delete the ${noun}`))
      return
    }
    toast.success(`${noun[0].toUpperCase()}${noun.slice(1)} deleted`)
    setToDelete(null)
    load()
  }

  const set = (name: string, value: unknown) => setForm((prev) => ({ ...prev, [name]: value }))
  const canEdit = can(editing ? permissions.update : permissions.create)

  return (
    <div className="space-y-4">
      <PageHeader title={title} description={description} onAddClick={openCreate} addPermission={permissions.create} addButtonText={`Add ${noun}`} />

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder ?? 'Search'}
              aria-label={searchPlaceholder ?? 'Search'}
              className="w-full sm:w-64"
            />
            {children}
          </div>
        </CardHeader>
        <CardContent>
          {!loading && rows.length === 0 ? (
            <NoData title={`No ${noun}s found`} btnText={`Add ${noun}`} openCreateDialog={openCreate} createPermission={permissions.create} />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    {columns.map((c) => (
                      <TableHead key={c.label} className={c.className}>{c.label}</TableHead>
                    ))}
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading
                    ? Array.from({ length: 4 }, (_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: columns.length + 1 }, (_, j) => (
                            <TableCell key={j}><Skeleton className="h-4 w-3/4" /></TableCell>
                          ))}
                        </TableRow>
                      ))
                    : rows.map((row) => (
                        <TableRow key={row._id}>
                          {columns.map((c) => (
                            <TableCell key={c.label} className={c.className}>{c.render(row)}</TableCell>
                          ))}
                          <TableCell className="text-right">
                            <ActionDropdown
                              items={[
                                { label: 'Edit', icon: Pencil, permission: permissions.update, onClick: () => openEdit(row) },
                                ...(rowActions?.(row, load) ?? []),
                                { label: 'Delete', icon: Trash2, permission: permissions.delete, variant: 'destructive', onClick: () => setToDelete(row) },
                              ]}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                </TableBody>
              </Table>
              <Pagination.Buttons currentPage={page} totalItems={count} pageSize={PAGE_SIZE} onPageChange={setPage} />
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${noun}` : `Add ${noun}`}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {fields.map((field) => {
              const id = `resource-${field.name}`
              const value = form[field.name]
              const label = (
                <Label htmlFor={id}>
                  {field.label}
                  {field.required ? ' *' : ''}
                </Label>
              )
              if (field.type === 'switch' || field.type === 'status') {
                return (
                  <div key={field.name} className="flex items-center gap-3 sm:col-span-2">
                    <Switch id={id} checked={!!value} onCheckedChange={(v) => set(field.name, v)} disabled={!canEdit} />
                    {label}
                    {field.help && <span className="text-sm text-muted-foreground">{field.help}</span>}
                  </div>
                )
              }
              return (
                <div key={field.name} className={field.type === 'textarea' ? 'space-y-1.5 sm:col-span-2' : 'space-y-1.5'}>
                  {label}
                  {field.type === 'textarea' ? (
                    <Textarea id={id} value={String(value ?? '')} onChange={(e) => set(field.name, e.target.value)} placeholder={field.placeholder} rows={3} disabled={!canEdit} />
                  ) : field.type === 'select' ? (
                    <Select value={String(value ?? '')} onValueChange={(v) => set(field.name, v)} disabled={!canEdit}>
                      <SelectTrigger id={id}>
                        <SelectValue placeholder={field.placeholder ?? 'Select'} />
                      </SelectTrigger>
                      <SelectContent>
                        {(field.options ?? []).map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id={id}
                      type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : 'text'}
                      min={field.min}
                      max={field.max}
                      value={String(value ?? '')}
                      onChange={(e) => set(field.name, e.target.value)}
                      placeholder={field.placeholder}
                      disabled={!canEdit}
                    />
                  )}
                  {field.help && <p className="text-xs text-muted-foreground">{field.help}</p>}
                </div>
              )
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            {canEdit && (
              <Button onClick={save} disabled={saving}>
                {saving && <Spinner />}
                Save
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!toDelete}
        onOpenChange={(open) => !open && setToDelete(null)}
        title={`Delete ${noun}?`}
        description={deleteDescription ?? `This moves the ${noun} to the trash.`}
        confirmText={deleting ? 'Deleting...' : 'Delete'}
        onConfirm={confirmDelete}
        loading={deleting}
        variant="destructive"
      />
    </div>
  )
}
